import React from 'react';
import { toast } from 'react-toastify';
import irelandApi from '../irelandApi';
import store, { history } from '../store';
import {clear, currentUserGroup, isPseudoLoggedIn, setAuthorizationToken} from '../utils/tokenUtils';
import { getMyProfile, getAdminMyProfile } from './user';
import { PIN, LINK } from '../../src/data/resetTypes';
import ToastMessage from '../components/common/toast-message';
import { REP_USER_GROUPS } from '../data/userGroups';

export const login = payload => irelandApi.post('/api/users/sign_in?login_source=web', {
  user: payload
})
  .then((res) => {
    const token = res.headers.authorization.split(' ')[1];

    const targetPath = store.getState().viewConfig.requestedPath ? store.getState().viewConfig.requestedPath : '/';
    store.dispatch({type: 'CLEAR_REQUESTED_PATH'});

    setAuthorizationToken(token);
    store.dispatch(getMyProfile('CURRENT_USER_PROFILE'))
      .then(history.push(targetPath));
  })
  .catch(error => Promise.reject(error.response.data));


const finishImpersonation = () => {
  const redirect = REP_USER_GROUPS.includes(currentUserGroup()) ? '/team' : '/';
  return store.dispatch(getMyProfile('CURRENT_USER_PROFILE')).then(() => history.push(redirect));
};

export const adminLogin = payload => irelandApi.post('/api/admin/users/sign_in?login_source=web', {
  user: payload
})
  .then((res) => {
    const token = res.headers.authorization.split(' ')[1];
    setAuthorizationToken(token);
    sessionStorage.removeItem('pending-impersonation');

    if (isPseudoLoggedIn()) {
      return finishImpersonation();
    }

    const isAuditor = res.data.data.attributes['is-auditor'];
    const path = isAuditor ? '/admin/audit/submissions/next' : '/admin/my-profile';
    const targetPath = store.getState().viewConfig.requestedPath ? store.getState().viewConfig.requestedPath : path;

    store.dispatch({ type: 'CLEAR_REQUESTED_PATH' });
    return store.dispatch(getAdminMyProfile('CURRENT_USER_PROFILE'))
      .then(() => history.push(targetPath));
  })
  .catch(error => Promise.reject(error.response.data));


export const teamLogin = payload => irelandApi.post('/api/team/users/sign_in?login_source=web', {
  user: payload
})
  .then((res) => {
    const token = res.headers.authorization.split(' ')[1];
    setAuthorizationToken(token);
    sessionStorage.removeItem('pending-impersonation');

    if (isPseudoLoggedIn()) {
      return finishImpersonation();
    }

    return store.dispatch(getMyProfile('CURRENT_USER_PROFILE'))
      .then(() => history.push('/team'));
  })
  .catch(error => Promise.reject(error.response.data));


export const logout = () => (dispatch) => {
  dispatch({type: 'DELETE_LOGOUT_PENDING'});
  return irelandApi.delete('/api/users/sign_out')
    .then(() => {
      clear();
      dispatch({type: 'DELETE_LOGOUT_FULFILLED'});
    })
    .catch((error) => {
      dispatch({type: 'DELETE_LOGOUT_REJECTED'});
      Promise.reject(error.response.data);
    });
};

export const adminLogout = () => (dispatch) => {
  dispatch({type: 'DELETE_LOGOUT_PENDING'});
  return irelandApi.delete('/api/admin/users/sign_out')
    .then(() => {
      clear();
      dispatch({type: 'DELETE_LOGOUT_FULFILLED'});
    })
    .catch((error) => {
      dispatch({type: 'DELETE_LOGOUT_REJECTED'});
      Promise.reject(error.response.data);
    });
};

export const repLogout = () => (dispatch) => {
  dispatch({type: 'DELETE_LOGOUT_PENDING'});
  return irelandApi.delete('/api/team/users/sign_out')
    .then(() => {
      clear();
      dispatch({type: 'DELETE_LOGOUT_FULFILLED'});
    })
    .catch((error) => {
      dispatch({type: 'DELETE_LOGOUT_REJECTED'});
      Promise.reject(error.response.data);
    });
};

export const requestReset = (payLoad, redirectUrl = '/') => {
  let data = {};

  if (payLoad) {
    if (payLoad.email) {
      data.email = payLoad.email;
    }
    if (payLoad.mobile) {
      data.mobile = payLoad.mobile;
    }
  } else {
    data = store.getState().resetState.payLoad;
  }

  store.dispatch({
    type: 'PW_RESET_VALUE_PENDING',
    payLoad: data
  });

  return irelandApi.post('/api/users/password', data)
    .then((response) => {
      if (response.data.reset_type === PIN) {
        history.push(redirectUrl);
      } else if (response.data.reset_type === LINK) {
        let toastMessage = 'Please check your emails to find your password reset link';

        if (data.mobile) {
          toastMessage = 'Please check your mobile to find your password reset link';
        }

        toast.success(<ToastMessage iconStyle="info" message={toastMessage}/>);
        history.push('/');
      }
      store.dispatch({
        type: 'PW_RESET_VALUE_FULFILLED',
        payLoad: response.data
      });
    });
};

export const verifyPIN = (payLoad, redirectUrl = '/') => {
  store.dispatch({
    type: 'VERIFY_PIN_PENDING',
    payload: []
  });

  const data = {
    pin: payLoad.pin,
    state: payLoad.state || store.getState().resetState.resetState
  };

  return irelandApi.post('/api/users/password/confirm', {
    user: data
  })
    .then((response) => {
      store.dispatch({
        type: 'VERIFY_PIN_FULFILLED',
        payLoad: response.data
      });
      history.push(redirectUrl);
    });
};

export const getQuestions = () => (dispatch) => {
  dispatch({
    type: 'SEQ_PENDING',
    payload: []
  });

  return irelandApi.get(`/api/security_questions?state=${store.getState().verifyPIN.state}`)
    .then((response) => {
      dispatch({
        type: 'SEQ_FULFILLED',
        payLoad: response.data
      });
    })
    .catch((error) => {
      dispatch({
        type: 'SEQ_REJECTED',
        payLoad: error
      });
    });
};

export const verifyQuestions = (payLoad, redirectUrl = '') => {
  store.dispatch({
    type: 'VERIFY_ANSWERS_PENDING',
    payload: []
  });

  const data = {
    state: store.getState().verifyPIN.state,
    security_questions: [
      {
        security_question_id: store.getState().questions.questions.question_id1,
        answer: payLoad.answer1
      },
      {
        security_question_id: store.getState().questions.questions.question_id2,
        answer: payLoad.answer2
      }]
  };

  return irelandApi.post('/api/users/password/security_questions', {
    user: data
  })
    .then((response) => {
      store.dispatch({
        type: 'VERIFY_ANSWERS_FULFILLED',
        payLoad: response.data
      });
      history.push(redirectUrl);
    });
};


export const newPassword = (payLoad, resetType, redirectUrl = '/') => {
  let pendingAction = 'NEW_PASSWORD_PENDING';
  let fulfilledAction = 'NEW_PASSWORD_FULFILLED';
  let postLoginUrl = redirectUrl;
  let updateUrl = '/api/users/password';
  let data = {
    state: store.getState().questions.answers.state,
    ...payLoad,
  };

  if (resetType && resetType === LINK) {
    updateUrl = '/api/users/password/link_update';
    pendingAction = 'NEW_PASSWORD_VIA_LINK_PENDING';
    fulfilledAction = 'NEW_PASSWORD_VIA_LINK_FULFILLED';
    postLoginUrl = '/';
    data = {
      reset_password_token: store.getState().verifyResetLink.key,
      ...payLoad,
    };
  }

  store.dispatch({
    type: pendingAction,
    payload: []
  });

  return irelandApi.put(updateUrl, {user: data})
    .then((response) => {
      const token = response.headers.authorization.split(' ')[1];
      setAuthorizationToken(token);
      store.dispatch(getMyProfile('CURRENT_USER_PROFILE'));
      store.dispatch({
        type: fulfilledAction,
        payLoad: response.data
      });
      toast.success(<ToastMessage iconStyle="success" message="Your password has been updated"/>);
      history.push(postLoginUrl);
    });
};

export const verifyResetLink = (payLoad) => {
  store.dispatch({
    type: 'VERIFY_RESET_LINK_PENDING',
    payLoad: payLoad.reset_password_token
  });

  const data = {
    ...payLoad,
  };

  return irelandApi.post('/api/users/password/reset_link', data)
    .then(() => {
      store.dispatch({
        type: 'VERIFY_RESET_LINK_FULFILLED',
        payLoad: []
      });
    }).catch((error) => {
      store.dispatch({
        type: 'VERIFY_RESET_LINK_REJECTED',
        payLoad: error.response.data
      });
      toast.error(<ToastMessage iconStyle="error" message={error.response.data.message}/>);
      history.push('/');
    });
};
